import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { PRODUCT_IMAGES, WHATSAPP_LINK } from "../../lib/constants";
import { ChevronDown, Clock, Gift, Sparkles } from "lucide-react";

const HeroSection = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // Countdown to midnight
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight - now;

      return {
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / 1000 / 60) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const scrollToProducts = () => {
    document.getElementById("productos")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={PRODUCT_IMAGES.trayBrownies}
          alt="Urban Brownies"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2D1810]/90 via-[#2D1810]/50 to-[#2D1810]/30" />
      </div>

      {/* Floating decorative elements */}
      <motion.div
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-20 right-10 md:right-20 opacity-20"
      >
        <Sparkles className="w-20 h-20 text-[#C88037]" />
      </motion.div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Urgency Badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="inline-flex items-center gap-2 bg-[#C88037]/90 text-white px-4 py-2 rounded-full text-sm font-medium mb-6 backdrop-blur-sm"
          data-testid="promo-badge"
        >
          <Gift className="w-4 h-4" strokeWidth={1.5} />
          <span>Promo Misteriosa: Pide hoy y recibe algo especial</span>
        </motion.div>

        {/* Main Headline - Curiosity Gap */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-tight mb-6"
        >
          ¿Sabías que existe un brownie que{" "}
          <span className="text-[#C88037] italic">nadie puede comer solo uno?</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-8"
        >
          Brownies artesanales hechos con amor en Bucaramanga. Cada mordisco es un abrazo al corazón.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10"
        >
          <motion.a
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto bg-[#C88037] text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg shadow-[#C88037]/30 hover:bg-[#E5984A] transition-colors flex items-center justify-center gap-2"
            data-testid="hero-cta-primary"
          >
            <span>Quiero Probar</span>
            <Sparkles className="w-5 h-5" strokeWidth={1.5} />
          </motion.a>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToProducts}
            className="w-full sm:w-auto border-2 border-white/50 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-colors"
            data-testid="hero-cta-secondary"
          >
            Ver el Menú
          </motion.button>
        </motion.div>

        {/* Countdown Timer - FOMO */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-[#2D1810]/80 backdrop-blur-md rounded-2xl p-6 max-w-md mx-auto border border-[#C88037]/30"
          data-testid="countdown-timer"
        >
          <div className="flex items-center justify-center gap-2 text-[#C88037] mb-3">
            <Clock className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-sm font-medium uppercase tracking-wide">
              La promo misteriosa termina en:
            </span>
          </div>
          <div className="flex items-center justify-center gap-4">
            <TimeBlock value={timeLeft.hours} label="Horas" />
            <span className="text-3xl text-white/50">:</span>
            <TimeBlock value={timeLeft.minutes} label="Min" />
            <span className="text-3xl text-white/50">:</span>
            <TimeBlock value={timeLeft.seconds} label="Seg" />
          </div>
          <p className="text-white/60 text-xs mt-3">
            * Pide antes de medianoche y recibe un brownie misterioso de regalo
          </p>
        </motion.div>

        {/* Scroll indicator */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ delay: 1, y: { duration: 2, repeat: Infinity } }}
          onClick={scrollToProducts}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors"
          data-testid="scroll-indicator"
        >
          <ChevronDown className="w-8 h-8" strokeWidth={1.5} />
        </motion.button>
      </div>
    </section>
  );
};

// Time Block Component
const TimeBlock = ({ value, label }) => (
  <div className="text-center">
    <div className="bg-[#C88037] text-white text-3xl md:text-4xl font-bold w-16 h-16 md:w-20 md:h-20 rounded-xl flex items-center justify-center shadow-lg">
      {String(value).padStart(2, "0")}
    </div>
    <span className="text-white/60 text-xs mt-1 block">{label}</span>
  </div>
);

export default HeroSection;
